import cn from 'classnames';
import React, { ChangeEvent, Ref, useRef, useState } from 'react';

import { LinkButton } from '@eika/button';
import { useViewType, ViewType, useDetectClickOutsideAndKeyDownEscOrTab } from '@eika/hooks';
import { Input, SearchInput, InputProps } from '@eika/input';
import '@eika/input-style/src/search-select.scss';
import { Modal } from '@eika/modal';

import SuggestionList from './SuggestionList';

export interface SearchSelectProps extends InputProps {
    /** Must be set to your app's namespace. I.e. 'no-eika-lokalbank'. Required by sitecore. */
    appNamespace: string;
    /** List of autocomplete options. */
    suggestList: {
        value: string;
        id?: string;
        ariaLabel?: string;
    }[];
    value: string;
    /** Unique id attribute of the suggest list. */
    list: string;
    /** Function that is performed on input onChange, must set value and adjust suggestList depending on value. */
    onInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
    /** Function that is performed on button onClick in suggested list, must set value */
    onButtonSelect: (value: string) => void;
    /** If undefined or false: Input-component, if true: SearchInput-component  */
    useSearchInput?: boolean;
    className?: string;
    onSuccess?: () => void;
    insideModal?: boolean;
}

const INPUT_ID = 'INPUT_ID';
const INPUT_ID_IN_MODAL = 'INPUT_ID_IN_MODAL';

/** @deprecated use ComboBox instead */
const SearchSelect = React.forwardRef(
    (
        {
            appNamespace,
            suggestList,
            value,
            list,
            useSearchInput = false,
            onInputChange,
            onButtonSelect,
            className,
            onSuccess,
            insideModal = false,
            ...rest
        }: SearchSelectProps,
        ref: Ref<HTMLInputElement>,
    ): JSX.Element => {
        const [showModal, setShowModal] = useState(false);
        const [showOverlay, setShowOverlay] = useState(false);

        const viewType: ViewType = useViewType();

        const successCallback = () => {
            if (onSuccess) onSuccess();
        };

        const onDesktopInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            onInputChange(event);
            if (event.target.value.length > 0 && suggestList.length > 0) {
                setShowOverlay(true);
            } else {
                setShowOverlay(false);
            }
        };

        const onDesktopInputFocus = () => {
            setShowOverlay(value.length > 0);
        };

        const onMobileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
            onInputChange(event);
            if (!showModal) {
                setShowModal(true);
            }
        };

        const searchSelectRef = useRef(null);
        const inputRef = useRef<HTMLInputElement>(null);
        useDetectClickOutsideAndKeyDownEscOrTab(searchSelectRef, () => {
            setShowOverlay(false);
        });

        const inputMobileProps = {
            ...rest,
            ref,
            value,
            onChange: onMobileInputChange,
        };

        const inputDesktopProps = {
            ...rest,
            error: !showOverlay && rest.error,
            ref,
            value,
            onChange: onDesktopInputChange,
            onFocus: onDesktopInputFocus,
            list,
        };

        return (
            <div className={cn('sgw-search-select', { className })}>
                {viewType === ViewType.mobile ? (
                    <>
                        {useSearchInput ? (
                            <SearchInput id={INPUT_ID} {...inputMobileProps} onClick={() => setShowModal(true)} />
                        ) : (
                            <Input id={INPUT_ID} {...inputMobileProps} onClick={() => setShowModal(true)} />
                        )}
                        <Modal
                            type={insideModal ? 'sub' : undefined}
                            id="search-modal"
                            shouldCloseOnOverlayClick
                            open={showModal}
                            setOpen={setShowModal}
                            appNamespace={appNamespace}
                            onTransitionEnd={(e) => {
                                if (insideModal) {
                                    e.stopPropagation();
                                    inputRef.current?.focus();
                                }
                            }}
                        >
                            <div className="search-input-and-cancel-button">
                                {useSearchInput ? (
                                    <SearchInput
                                        id={INPUT_ID_IN_MODAL}
                                        {...inputMobileProps}
                                        ref={insideModal ? inputRef : undefined}
                                        list={list}
                                        onTransitionEnd={(e) => e.stopPropagation()}
                                    />
                                ) : (
                                    <Input
                                        id={INPUT_ID_IN_MODAL}
                                        {...inputMobileProps}
                                        ref={insideModal ? inputRef : undefined}
                                        list={list}
                                        onTransitionEnd={(e) => e.stopPropagation()}
                                    />
                                )}
                                <LinkButton type="button" onClick={() => setShowModal(false)}>
                                    Avbryt
                                </LinkButton>
                            </div>
                            <SuggestionList
                                suggestList={suggestList}
                                inputValue={value}
                                coreSuggestId={list}
                                onButtonSelect={onButtonSelect}
                                showModal={showModal}
                                setShowModal={(showModal) => {
                                    setShowModal(showModal);
                                    successCallback();
                                }}
                            />
                        </Modal>
                    </>
                ) : (
                    <div
                        ref={searchSelectRef}
                        className={cn({
                            'search-select--overlay': showOverlay && suggestList.length > 0,
                        })}
                    >
                        {useSearchInput ? <SearchInput {...inputDesktopProps} /> : <Input {...inputDesktopProps} />}
                        {showOverlay && suggestList.length > 0 && (
                            <SuggestionList
                                suggestList={suggestList}
                                inputValue={value}
                                coreSuggestId={list}
                                onButtonSelect={onButtonSelect}
                                showOverlay={showOverlay}
                                setShowOverlay={(showOverlay) => {
                                    setShowOverlay(showOverlay);
                                    successCallback();
                                }}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    },
);

export default SearchSelect;
